import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { usernameFromEmail } from './IspAccountForm';
import {
   getIspTenant,
   getIspPlans,
   getIspCustomers,
   createIspCustomer,
   updateIspCustomer,
   deleteIspCustomer,
   decideIspCustomer,
   createIspAccount,
   type IspCustomerRow,
   type IspAccountDraft,
} from '@/api/isp';
import { useUIStore } from '@/store/useUIStore';
import { useIspGate } from './useIspGate';
import styled from 'styled-components';
import { IspPage } from './ispLayout';
import IspPager, { PAGE_SIZE } from './IspPager';
import { colors } from '@/constants/colors';
import { type CustomerDraft } from './IspCustomerForm';
import { RowActions, actionColumn } from './ispSettingsUI';
import {
   MOCK_CUSTOMERS,
   STATUS_LABEL,
   type IspCustomer,
   type IspCustomerStatus,
} from './ispMockData';

/**
 * ISP Business — Customers.
 *
 * The people and businesses paying for internet. Their CONNECTIONS live on the
 * Accounts page: one customer can hold several, which is why this list shows an
 * account count rather than a plan or a PPPoE login.
 *
 * UI first, on mock data (Ray, 2026-08-28) — the shape is the server model, so
 * wiring the API later replaces where the rows come from and nothing else.
 */

// A customer's own states are an approval, not a connection state — being cut
// off belongs to an account. Pending is the one an operator acts on: somebody
// signed up through a public form and is waiting to be let in.
/**
 * 🔴 NO 'pending' HERE.
 *
 * Ray, 2026-09-01: *"the pending items, dont mix it in main and in filter thats
 * bad user experience, it should be separate table above the main listing, just
 * like adding pending vendo."* An application is a JOB, not a record — mixing
 * it into a list of customers means the work is only visible to somebody who
 * thinks to filter for it, and a queue nobody sees is a queue nobody works.
 */
const FILTERS: ('all' | IspCustomerStatus)[] = ['all', 'approved', 'rejected'];

const IspCustomers: React.FC = () => {
   const qc = useQueryClient();
   const navigate = useNavigate();
   /**
    * From the server, not browser state.
    *
    * 🔴 THIS LIST USED TO BE MOCK_CUSTOMERS IN useState. Ray, 2026-08-31: "when
    * i delete customer it is normal it doenst save to database? since when i
    * reload it returns back." It was not normal — nothing had ever been asked to
    * forget them, and every edit and every delete lived until the next refresh.
    */
   const { data: rows = [] } = useQuery({
      queryKey: ['isp-customers'],
      queryFn: getIspCustomers,
   });

   // Needed to turn the plan an applicant CHOSE into a name the operator reads.
   const { data: plans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });

   // For the sign-up link — the operator's own portal slug.
   const { data: tenant } = useQuery({
      queryKey: ['isp-tenant'],
      queryFn: getIspTenant,
   });
   const [query, setQuery] = useState('');
   const [filter, setFilter] = useState<'all' | IspCustomerStatus>('all');
   /**
    * Deleting asks in a MODAL, not an inline panel below the table.
    *
    * The old confirmation rendered under the list, so on any real number of
    * customers it appeared where nobody was looking, about a row already
    * scrolled past.
    */
   /*
    * 🔴 THE OLD DIALOG SENT THEM TO A BUTTON THAT NO LONGER EXISTS. It said
    * "Disconnect those first" while offering "Yes, delete" — which answered
    * 409 — and Disconnect had been taken off the Accounts page in September.
    *
    * Ray, 2026-09-05: *"when they delete the customer it show a modal popup…
    * then in the modal it has list of the accounts list, and it has disconnect
    * button, so after he disconnects those items the delete will enable."* So
    * the dead end becomes the place the work is done.
    */
   const confirmRemove = (c: IspCustomerRow) =>
      handleShow({
         template: 'isp-customer-delete',
         title: `Delete ${c.fullName}?`,
         windowType: 'desktop',
         data: {
            customerId: c.id,
            customerName: c.fullName,
            onCancel: handleClose,
            onDelete: (id: string) => removeCustomer.mutateAsync(id),
            onDeleted: handleClose,
         },
      });
   const { handleShow, handleClose } = useUIStore();
   const { gate } = useIspGate();

   /**
    * 🔴 RESET ON EVERY FILTER CHANGE. Ray, 2026-09-03: *"make sure accounts,
    * customers, billing has pagination."* Without the reset, filtering from
    * 300 rows down to 4 while on page 6 shows an empty table and an operator
    * who believes the filter found nothing.
    */
   const [page, setPage] = useState(0);

   const shown = useMemo(() => {
      const q = query.trim().toLowerCase();
      return rows.filter((r) => {
         // Pending has its own table above; showing it twice would make the
         // counts disagree with what an operator is looking at.
         if (r.status === 'pending') return false;
         if (filter !== 'all' && r.status !== filter) return false;
         if (!q) return true;
         // A cashier searches by whatever the customer says on the phone —
         // their name, their number, or the account number on the bill.
         return (
            r.fullName.toLowerCase().includes(q) ||
            r.customerCode.toLowerCase().includes(q) ||
            r.phone.toLowerCase().includes(q) ||
            r.address.toLowerCase().includes(q)
         );
      });
   }, [rows, query, filter]);

   const paged = useMemo(
      () => shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      [shown, page]
   );

   // A filter that narrows the list must take the reader back to its start,
   // or they land on a page that no longer exists and read it as "nothing
   // found".
   useEffect(() => {
      setPage(0);
   }, [shown.length]);

   /**
    * Saving a NEW customer goes straight on to giving them a connection.
    *
    * Ray, 2026-08-31: *"in the customer registration, we also have to include
    * proceed account creation in that one customer modal right?"* Yes — a
    * customer is created IN ORDER to give them a line. Making somebody save,
    * navigate to Accounts and search for the person they just typed in is three
    * screens for one job, and the third is where they give up.
    *
    * Only on CREATE. Editing an existing customer's phone number is not a
    * request for another connection.
    */
   const saveCustomer = useMutation({
      mutationFn: (v: { id: string | null; draft: CustomerDraft }) =>
         v.id
            ? updateIspCustomer(v.id, v.draft as never)
            : createIspCustomer(v.draft as never),
      onSuccess: (saved, v) => {
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         qc.invalidateQueries({ queryKey: ['isp-readiness'] });
         toast.success('Saved.');
         if (!v.id) openAccountFor(saved);
         else handleClose();
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save the customer.'
         ),
   });

   const removeCustomer = useMutation({
      mutationFn: (id: string) => deleteIspCustomer(id),
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         toast.success('Customer removed.');
      },
      // The 409 here names how many connections they still hold, and points at
      // Disconnected as the honest alternative. Shown verbatim.
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not remove the customer.'
         ),
   });

   /**
    * The operator's answer to an application.
    *
    * Approving CONNECTS NOBODY — it only makes somebody eligible. The plan and
    * the MikroTik are still chosen afterwards, which is the step that writes to
    * a live router, and the toast says so rather than leaving an operator to
    * assume the customer is online.
    */
   const decide = useMutation({
      mutationFn: (v: {
         id: string;
         decision: 'approved' | 'rejected';
         reason?: string;
         then?: IspCustomerRow;
      }) => decideIspCustomer(v.id, v.decision, v.reason),
      onSuccess: (c, v) => {
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         if (v.decision === 'approved') {
            // 🔴 Straight on to the connection, not back to a list. Ray,
            // 2026-09-01: *"when approve it should continue to the account
            // creation/connection form."* Approving exists in order to connect
            // somebody; ending on a table makes them find the person again.
            toast.success(`${c.fullName} approved.`);
            openAccountFor(c);
            return;
         }
         handleClose();
         toast.success(`${c.fullName} rejected.`);
      },
      // The 409 names how many connections they still hold. Shown verbatim.
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save that decision.'
         ),
   });

   const addAccount = useMutation({
      mutationFn: (draft: IspAccountDraft) => createIspAccount(draft),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-customers'] });
         qc.invalidateQueries({ queryKey: ['isp-readiness'] });
         handleClose();
         // The line is written to the router as it saves, so the toast reports
         // what actually reached the hardware — an account that only exists in
         // SmartFi bills somebody who cannot get online.
         if (r.sync.ok) toast.success(r.sync.message);
         else toast.warning(r.sync.message);
      },
      /*
       * 🔴 A SHORTFALL MUST NOT BE A DEAD END. Ray, 2026-09-04: *"it should
       * redirect to billing page if this toast error show."*
       *
       * A 402 means SmartFi refused the line for want of points, and the only
       * place to do anything about that is Billing. Being told the price with
       * no way to pay it is the same dead end the Billing page's own convert
       * button exists to close.
       *
       * ⚠️ IT DOES NOT REDIRECT BY ITSELF. The connection form is still open
       * and filled in — PPPoE username, plan, collection day — and navigating
       * away would throw all of it away, so an operator who tops up returns to
       * an empty form and retypes everything. The toast is the door instead:
       * one click, and it stays on screen long enough to find.
       */
      onError: (e: unknown) => {
         const err = e as {
            response?: { status?: number; data?: { error?: string } };
         };
         const msg = err?.response?.data?.error ?? 'Could not add the connection.';
         if (err?.response?.status === 402) {
            toast.error(`${msg} — tap here to open Subscriptions.`, {
               autoClose: 12000,
               onClick: () => navigate('/isp-billing'),
               style: { cursor: 'pointer' },
            });
            return;
         }
         toast.error(msg);
      },
   });

   /** Straight from a saved customer into their first connection. */
   /** Whole days since they applied. 0 = today. */
   const waitingDays = (iso: string | null) =>
      iso
         ? Math.max(
              0,
              Math.floor(
                 (new Date().setHours(0, 0, 0, 0) -
                    new Date(iso).setHours(0, 0, 0, 0)) /
                    86400000
              )
           )
         : 0;

   const waitingLabel = (iso: string | null) => {
      if (!iso) return '—';
      const d = waitingDays(iso);
      return d === 0 ? 'Today' : d === 1 ? '1 day' : `${d} days`;
   };

   const planName = (id: string) =>
      id ? (plans.find((p) => p.id === id)?.name ?? null) : null;

   /**
    * Approving from a table row means approving somebody whose ID you have not
    * looked at. Ray, 2026-09-01: *"it should show an action review details
    * because its important to see specially the ID."*
    */
   /**
    * The public sign-up link, copied from where an operator actually is.
    *
    * Built from window.location so it is right on staging, on prod and on a
    * laptop without a build-time constant that would be quietly wrong on one.
    */
   const applyUrl = `${window.location.origin}/isp/${tenant?.portalSlug ?? ''}/apply`;
   const shareLink = () => {
      navigator.clipboard
         ?.writeText(applyUrl)
         .then(() => toast.success('Sign-up link copied. Share it anywhere.'))
         // Copying can be blocked; saying so beats a button that silently
         // does nothing.
         .catch(() =>
            toast.error('Could not copy — open the link and copy it by hand.')
         );
   };

   const openReview = (c: IspCustomerRow) =>
      handleShow({
         template: 'isp-application-review',
         title: `Application — ${c.fullName}`,
         windowType: 'desktop',
         data: {
            customer: c,
            wantedPlan: plans.find((p) => p.id === c.wantedPlan) ?? null,
            onCancel: handleClose,
            onApprove: () =>
               decide.mutate({ id: c.id, decision: 'approved', then: c }),
            onReject: (reason: string) =>
               decide.mutate({ id: c.id, decision: 'rejected', reason }),
         },
      });

   const openAccountFor = (c: IspCustomerRow) =>
      handleShow({
         template: 'isp-account-form',
         title: `First connection — ${c.fullName}`,
         windowType: 'desktop',
         data: {
            customerId: c.id,
            customerName: c.fullName,
            // Defaults, all overrulable: the plan they ASKED for, and a login
            // built from their email so it is one they can repeat on the phone.
            suggestedPlan: c.wantedPlan || undefined,
            suggestedUsername: usernameFromEmail(c.email) || undefined,
            onCancel: handleClose,
            onSave: (draft: IspAccountDraft) => addAccount.mutate(draft),
         },
      });

   const save = (existing: IspCustomerRow | null, draft: CustomerDraft) =>
      saveCustomer.mutate({ id: existing?.id ?? null, draft });

   // Same prompt shell as every other form in the panel — an operator should
   // not have to learn a second way for a form to appear.
   const openForm = (existing: IspCustomerRow | null) =>
      handleShow({
         template: 'isp-customer-form',
         title: existing ? `Edit — ${existing.fullName}` : 'New customer',
         windowType: 'desktop',
         data: {
            existing,
            onCancel: handleClose,
            onSave: (draft: CustomerDraft) => save(existing, draft),
         },
      });

   // Applications live above the list, never inside it.
   const pending = rows.filter((r) => r.status === 'pending');

   return (
      <IspPage>
         <TopRow>
            <Title>Customers</Title>
            {/* A customer with nowhere to be connected is a contact, not a
                subscriber. Ray, 2026-08-31: "adding customer, it should show a
                modal message, go to create server." */}
            <HeadActions>
               {/* Moved out of Settings. Ray, 2026-09-02: the link belongs
                   where customers are, not buried in a settings tab nobody
                   opens twice. */}
               {/* 🔴 A LINK, NOT A BUTTON. Ray, 2026-09-03: *"replace the
                   label to 'open sharable form' with icon indicator that can
                   be open externally, not an actual button UI."* A button beside
                   the primary action competes with it; this goes somewhere
                   else entirely, and the arrow says so before it is read. */}
               {tenant?.portalSlug ? (
                  <ExternalLink type="button" onClick={shareLink}>
                     Open sharable form
                     <ExtIcon viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M14 4h6v6" />
                        <path d="M20 4 10 14" />
                        <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                     </ExtIcon>
                  </ExternalLink>
               ) : null}
               <Primary
                  type="button"
                  onClick={() => gate('server+plan', () => openForm(null))}
               >
                  Add customer
               </Primary>
            </HeadActions>
         </TopRow>

         {pending.length > 0 && (
            <Waiting>
               <WaitHead>
                  <WaitTitle>
                     {pending.length} application
                     {pending.length === 1 ? '' : 's'} waiting
                  </WaitTitle>
                  <WaitNote>
                     Sent from your sign-up link. Review each one before
                     connecting them.
                  </WaitNote>
               </WaitHead>
               <TableWrap>
                  <Table>
                     <thead>
                        <tr>
                           <Th>Name</Th>
                           <Th>Contact</Th>
                           <Th>Wants</Th>
                           <Th>Waiting</Th>
                           <Th />
                        </tr>
                     </thead>
                     <tbody>
                        {pending.map((r) => (
                           <tr key={r.id}>
                              <Td>
                                 <strong>{r.fullName}</strong>
                                 {r.address && <Sub>{r.address}</Sub>}
                              </Td>
                              <Td>
                                 {r.phone || '—'}
                                 {r.email && <Sub>{r.email}</Sub>}
                              </Td>
                              <Td>
                                 {planName(r.wantedPlan) ?? (
                                    <Sub>Not chosen</Sub>
                                 )}
                              </Td>
                              {/* How long they have been ignored. An operator
                                  who cannot see this cannot tell a new
                                  application from a forgotten one. */}
                              <Td>
                                 <Age $stale={waitingDays(r.appliedAt) >= 3}>
                                    {waitingLabel(r.appliedAt)}
                                 </Age>
                              </Td>
                              <Td>
                                 <RowActions>
                                    <Primary
                                       type="button"
                                       onClick={() => openReview(r)}
                                    >
                                       Review
                                    </Primary>
                                 </RowActions>
                              </Td>
                           </tr>
                        ))}
                     </tbody>
                  </Table>
               </TableWrap>
               <IspPager
                  page={page}
                  total={shown.length}
                  onPage={setPage}
                  noun="customers"
               />
            </Waiting>
         )}

         <Controls>
            <Search
               value={query}
               placeholder="Search name, code, phone or address"
               onChange={(e) => setQuery(e.target.value)}
               aria-label="Search customers"
            />
            <Filters>
               {FILTERS.map((f) => (
                  <Chip
                     key={f}
                     type="button"
                     $on={filter === f}
                     onClick={() => setFilter(f)}
                  >
                     {f === 'all' ? 'All' : STATUS_LABEL[f]}
                  </Chip>
               ))}
            </Filters>
         </Controls>

         {shown.length === 0 ? (
            <Empty>
               {rows.length === 0
                  ? 'No customers yet. Add your first one above.'
                  : 'Nothing matches that search.'}
            </Empty>
         ) : (
            <TableWrap>
               <Table>
                  <thead>
                     <tr>
                        <Th>Code</Th>
                        <Th>Name</Th>
                        <Th>Contact</Th>
                                                <Th>Accounts</Th>
                        <Th>Status</Th>
                        <Th />
                     </tr>
                  </thead>
                  <tbody>
                     {paged.map((r) => (
                        <tr key={r.id}>
                           <Td $mono>{r.customerCode}</Td>
                           <Td>
                              <strong>{r.fullName}</strong>
                              {r.address && <Sub>{r.address}</Sub>}
                           </Td>
                           <Td>
                              {r.phone || '—'}
                              {r.email && <Sub>{r.email}</Sub>}
                           </Td>
                           <Td>
                              {/* One customer can hold several connections —
                                  a restaurant owner with two branches. */}
                              {r.accounts === 0 ? (
                                 <Muted>none yet</Muted>
                              ) : (
                                 r.accounts
                              )}
                           </Td>
                           <Td>
                              <Pill $status={r.status}>
                                 {STATUS_LABEL[r.status]}
                              </Pill>
                           </Td>
                           <Td>
                              <RowActions>
                                 {/* No Approve here any more: pending rows
                                     live in their own table above, and every
                                     approval goes through the review modal so
                                     nobody is approved unseen. */}
                                 <Link type="button" onClick={() => openForm(r)}>
                                    Edit
                                 </Link>
                                 <Danger
                                    type="button"
                                    onClick={() => confirmRemove(r)}
                                 >
                                    Delete
                                 </Danger>
                              </RowActions>
                           </Td>
                        </tr>
                     ))}
                  </tbody>
               </Table>
            </TableWrap>
         )}

         {/* Inline, not a browser dialog: the warning is the important part and
             it has to stay on screen beside what it is warning about. */}
      </IspPage>
   );
};

/**
 * Applications sit in their own bordered block above the list.
 *
 * Given a colour and a count because it is WORK, not data — the same reason the
 * pending-vendo queue looks the way it does. Ray, 2026-09-01: *"it should be
 * separate table above the main listing, just like adding pending vendo."*
 */
const Waiting = styled.div`
   border: 1px solid ${colors.PRIMARY};
   border-radius: 12px;
   padding: 14px 14px 4px;
   margin-bottom: 20px;
   background: rgba(55, 96, 249, 0.04);
`;
const WaitHead = styled.div`
   margin-bottom: 10px;
`;
const WaitTitle = styled.div`
   font-size: 15px;
   font-weight: 800;
`;
const WaitNote = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;
/** Turns red once it has been sitting for days — that is the actual failure. */
const Age = styled.span<{ $stale: boolean }>`
   font-size: 13px;
   font-weight: ${({ $stale }) => ($stale ? 800 : 500)};
   color: ${({ $stale }) => ($stale ? colors.RED : 'inherit')};
`;

const ExternalLink = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   border: none;
   background: none;
   padding: 0 4px;
   color: ${colors.PRIMARY};
   cursor: pointer;

   &:hover {
      text-decoration: underline;
   }
`;
const ExtIcon = styled.svg`
   width: 13px;
   height: 13px;
   fill: none;
   stroke: currentColor;
   stroke-width: 2;
   stroke-linecap: round;
   stroke-linejoin: round;
`;
const HeadActions = styled.div`
   display: flex;
   gap: 8px;
`;

export default IspCustomers;


const TopRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;

const Title = styled.h1`
   margin: 0;
   font-size: 20px;
`;

const Controls = styled.div`
   display: flex;
   gap: 10px;
   flex-wrap: wrap;
   align-items: center;
`;

const Search = styled.input`
   flex: 1 1 280px;
   padding: 9px 11px;
   border-radius: 8px;
   border: 1px solid var(--app-border);
   background: transparent;
   color: inherit;
   font-size: 14px;
`;

const Filters = styled.div`
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
`;

const Chip = styled.button<{ $on: boolean }>`
   padding: 7px 12px;
   border-radius: 999px;
   font-size: 13px;
   cursor: pointer;
   border: 1px solid
      ${({ $on }) => ($on ? colors.PRIMARY : 'var(--app-border)')};
   background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   color: ${({ $on }) => ($on ? colors.WHITE : 'inherit')};
`;

const TableWrap = styled.div`
   /* Wide tables scroll inside their own box rather than the page. */
   overflow-x: auto;
   border: 1px solid var(--app-border);
   border-radius: 10px;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 14px;
   ${actionColumn}
`;

const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY4};
   border-bottom: 1px solid var(--app-border);
   white-space: nowrap;
`;

const Td = styled.td<{ $mono?: boolean }>`
   padding: 10px 12px;
   border-bottom: 1px solid var(--app-border);
   vertical-align: top;
   ${({ $mono }) => $mono && 'font-variant-numeric: tabular-nums;'}
`;

const Sub = styled.span`
   display: block;
   font-size: 12px;
   color: ${colors.GRAY4};
   margin-top: 2px;
`;

const Muted = styled.span`
   color: ${colors.GRAY4};
`;

const Pill = styled.span<{ $status: IspCustomerStatus }>`
   display: inline-block;
   padding: 3px 9px;
   border-radius: 999px;
   font-size: 12px;
   white-space: nowrap;
   color: ${({ $status }) =>
      $status === 'approved'
         ? colors.SUCCESS
         : $status === 'rejected'
           ? colors.RED
           : colors.ORANGE};
   border: 1px solid currentColor;
`;

const Link = styled.button`
   border: none;
   background: none;
   padding: 0;
   font-size: 13px;
   color: ${colors.PRIMARY};
   cursor: pointer;
`;

const Danger = styled(Link)`
   color: ${colors.RED};
`;

const Empty = styled.p`
   margin: 0;
   padding: 40px 0;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY4};
`;

/**
 * The standard ISP page action, matching Servers and Accounts exactly.
 *
 * Ray, 2026-09-02: *"why that button is large than the smartfi standard? you
 * should uniform it."* It was 9px/16px at 14px/600 while every other page used
 * 8px/14px at 12px/700 — a difference nobody chose, which is the worst kind.
 */
const Primary = styled.button`
   padding: 8px 14px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
`;

const Ghost = styled.button`
   padding: 9px 16px;
   border-radius: 8px;
   border: 1px solid var(--app-border);
   background: transparent;
   color: inherit;
   font-size: 14px;
   cursor: pointer;
`;

const Confirm = styled.div`
   border: 1px solid ${colors.RED};
   border-radius: 10px;
   padding: 14px;
`;

const ConfirmTitle = styled.strong`
   display: block;
   font-size: 14px;
   margin-bottom: 6px;
`;

const ConfirmBody = styled.p`
   margin: 0 0 12px;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY4};
`;

const ConfirmRow = styled.div`
   display: flex;
   gap: 8px;
`;

const DangerBtn = styled(Primary)`
   background: ${colors.RED};
`;
